import React, { useEffect, useState, useMemo, useRef } from "react";
import SectionHeading from "../Common/SectionHeading";
import SideBar from "./SideBar";
import { useFlightContext } from "hooks/flight/useFlightContext";
import FlightCard from "./flightCard";
import AirlinePriceComparison from "./AirlinePriceComparison";
import { FaAngleLeft, FaAngleRight } from "react-icons/fa6";
import { BiFilterAlt } from "react-icons/bi";
import MobileFilters from "./FilterDrawer";
import { useTranslation } from "react-i18next";
import { getMinMaxPrice, getTotalFlyingTimeInSec } from "helpers/flightHelper";

const FlightSearchWrapper = () => {
    const { availabilityResults, availabilityLoading, availabilityError } =
        useFlightContext();
    const { allFlightCombinations: flights } = availabilityResults || {};
    const [baseFlights, setBaseFlights] = useState([]);
    const [filteredFlights, setFilteredFlights] = useState([]);
    const [offset, setOffset] = useState(0);
    const [limit] = useState(10);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPage] = useState(0);
    const [paginatedFlights, setPaginatedFlights] = useState([]);
    const [isOpenFilters, setIsOpenFilters] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const elementRef = useRef(null);
    const { t } = useTranslation();

    // ========== UNIFIED FILTER STATE ==========
    const [filters, setFilters] = useState({
        selectedAirlines: [],
        sortBy: {
            lowPrice: true,
            highPrice: false,
            fastest: false,
            slowest: false,
            smartest: false,
        },
        priceRange: [0, 100000],
        priceFilterActive: false,
        onwardStops: {
            direct: false,
            oneStop: false,
            twoStop: false,
        },
        returnStops: {
            direct: false,
            oneStop: false,
            twoStop: false,
        },
        multiDestStops: {},
        departureAirports: [],
        arrivalAirports: [],
        departureTimeSlots: {},
    });

    // Initialize base flights
    useEffect(() => {
        if (flights) {
            setBaseFlights(flights);
            setFilteredFlights(flights);
            const [minPrice, maxPrice] = getMinMaxPrice(flights);
            setFilters({
                selectedAirlines: [],
                sortBy: {
                    lowPrice: true,
                    highPrice: false,
                    fastest: false,
                    slowest: false,
                    smartest: false,
                },
                priceRange: [minPrice, maxPrice],
                priceFilterActive: false,
                onwardStops: {
                    direct: false,
                    oneStop: false,
                    twoStop: false,
                },
                returnStops: {
                    direct: false,
                    oneStop: false,
                    twoStop: false,
                },
                multiDestStops: {},
                departureAirports: [],
                arrivalAirports: [],
                departureTimeSlots: {},
            });
        }
    }, [flights]);

    useEffect(() => {
        const updateIsMobile = () => {
            const nextIsMobile = window.innerWidth < 992;
            setIsMobile(nextIsMobile);
            if (!nextIsMobile) {
                setIsOpenFilters(false);
            }
        };

        updateIsMobile();
        window.addEventListener("resize", updateIsMobile);
        return () => window.removeEventListener("resize", updateIsMobile);
    }, []);

    // ========== UNIFIED FILTERING LOGIC ==========
    useEffect(() => {
        if (!baseFlights || baseFlights.length === 0) return;

        let result = [...baseFlights];

        // 1. Filter by selected airlines
        if (filters.selectedAirlines.length > 0) {
            result = result.filter((flight) => {
                const airline =
                    flight?.validatingCarrier ||
                    flight?.airline ||
                    flight?.bounds?.[0]?.airlines?.[0];
                return airline && filters.selectedAirlines.includes(airline);
            });
        }

        // 2. Filter by price range
        if (filters.priceFilterActive) {
            result = result.filter((flight) => {
                const price = flight?.recommendation?.price?.detailed_price?.total || 0;
                return price >= filters.priceRange[0] && price <= filters.priceRange[1];
            });
        }

        // 3. Filter by onward stops
        const onwardActive = Object.values(filters.onwardStops).some(v => v);
        if (onwardActive) {
            result = result.filter((flight) => {
                const onwardSegments = flight.bounds[0]?.segments?.length || 0;
                return (
                    (filters.onwardStops.direct && onwardSegments === 1) ||
                    (filters.onwardStops.oneStop && onwardSegments === 2) ||
                    (filters.onwardStops.twoStop && onwardSegments > 2)
                );
            });
        }

        // 4. Filter by return stops
        const returnActive = Object.values(filters.returnStops).some(v => v);
        if (returnActive && result[0]?.bounds?.length > 1) {
            result = result.filter((flight) => {
                const returnSegments = flight.bounds[1]?.segments?.length || 0;
                return (
                    (filters.returnStops.direct && returnSegments === 1) ||
                    (filters.returnStops.oneStop && returnSegments === 2) ||
                    (filters.returnStops.twoStop && returnSegments > 2)
                );
            });
        }

        // 5. Filter by multi-destination stops
        const tripType = result[0]?.bounds?.length === 2 ? 
            (result[0].bounds[0]?.departure?.locationId === result[0].bounds[1]?.arrival?.locationId && 
             result[0].bounds[0]?.arrival?.locationId === result[0].bounds[1]?.departure?.locationId ? 'rt' : 'md') :
            (result[0]?.bounds?.length > 2 ? 'md' : 'ow');
        
        const isMultiDest = tripType === 'md';
        
        if (isMultiDest && filters.multiDestStops) {
            result = result.filter((flight) => {
                const boundsCount = flight.bounds?.length || 0;
                
                for (let i = 0; i < boundsCount; i++) {
                    const boundFilter = filters.multiDestStops?.[i];
                    if (!boundFilter) continue;

                    const selectedStops = [
                        boundFilter.direct ? 0 : null,
                        boundFilter.oneStop ? 1 : null,
                        boundFilter.twoStop ? 2 : null,
                    ].filter(n => n !== null);

                    if (selectedStops.length === 0) continue;

                    const stops = flight.bounds[i]?.segments?.length - 1;

                    if (!selectedStops.includes(stops)) {
                        return false;
                    }
                }
                return true;
            });
        }

        // 6. Filter by departure airports
        if (filters.departureAirports.length > 0) {
            result = result.filter((flight) =>
                filters.departureAirports.includes(
                    flight.bounds[0]?.segments?.[0]?.departure.locationId
                )
            );
        }

        // 7. Filter by arrival airports
        if (filters.arrivalAirports.length > 0) {
            result = result.filter((flight) => {
                const lastSegment =
                    flight.bounds[0].segments[
                        flight.bounds[0].segments.length - 1
                    ];
                return filters.arrivalAirports.includes(
                    lastSegment.arrival.locationId
                );
            });
        }

        // 8. Filter by departure time slots
        const getTimePeriod = (time) => {
            if (!time) return null;
            const h = parseInt(time.split(':')[0], 10);
            if (isNaN(h)) return null;
            if (h < 8) return 'earlyMorning';
            if (h < 16) return 'morning';
            return 'evening';
        };
        const timeSlotBounds = Object.keys(filters.departureTimeSlots || {});
        if (timeSlotBounds.length > 0) {
            result = result.filter((flight) => {
                for (const key of timeSlotBounds) {
                    const boundIndex = Number(key);
                    const slots = filters.departureTimeSlots[boundIndex];
                    if (!slots) continue;
                    const anySelected = slots.earlyMorning || slots.morning || slots.evening;
                    if (!anySelected) continue;
                    const depTime = flight.bounds[boundIndex]?.departure?.time;
                    if (!depTime) continue;
                    const period = getTimePeriod(depTime);
                    if (period === null) continue;
                    if (!slots[period]) return false;
                }
                return true;
            });
        }

        // 9. Sort results
        if (filters.sortBy.lowPrice) {
            result.sort(
                (a, b) =>
                    (a.recommendation?.price?.detailed_price?.total || 0) -
                    (b.recommendation?.price?.detailed_price?.total || 0)
            );
        } else if (filters.sortBy.highPrice) {
            result.sort(
                (a, b) =>
                    (b.recommendation?.price?.detailed_price?.total || 0) -
                    (a.recommendation?.price?.detailed_price?.total || 0)
            );
        } else if (filters.sortBy.fastest) {
            result.sort((a, b) => {
                const aTime = getTotalFlyingTimeInSec(a);
                const bTime = getTotalFlyingTimeInSec(b);
                return aTime - bTime;
            });
        } else if (filters.sortBy.slowest) {
            result.sort((a, b) => {
                const aTime = getTotalFlyingTimeInSec(a);
                const bTime = getTotalFlyingTimeInSec(b);
                return bTime - aTime;
            });
        }

        setFilteredFlights(result);
    }, [baseFlights, filters]);

    // Pagination
    useEffect(() => {
        const totalFlights = filteredFlights?.length || 0;
        setTotalPage(Math.max(1, Math.ceil(totalFlights / limit)));
        setPaginatedFlights(
            filteredFlights?.slice(offset, offset + limit) || []
        );
    }, [filteredFlights, offset, limit]);

    useEffect(() => {
        setOffset(0);
        setPage(1);
    }, [filteredFlights]);
    useEffect(() => {
        // Scroll to the element when component mounts        
        if (elementRef.current) {
            elementRef.current.scrollIntoView({
                behavior: "smooth",
                block: "start",
                inline: "nearest",
            });
        }
    }, []);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
            setOffset((newPage - 1) * limit);
            window.scrollTo({
                top: 0,
                behavior: "smooth",
            });
        }
    };

    // Handle airline selection from top component OR sidebar
    const handleSelectAirline = (airline) => {
        setFilters((prev) => ({
            ...prev,
            selectedAirlines: prev.selectedAirlines.includes(airline)
                ? prev.selectedAirlines.filter((code) => code !== airline)
                : [...prev.selectedAirlines, airline]
        }));
    };

    const renderPagination = useMemo(() => {
        const maxVisiblePages = 5;
        const pages = [];

        if (totalPages <= maxVisiblePages + 2) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(
                    <li
                        key={i}
                        className="page-item mb-0"
                        onClick={() => handlePageChange(i)}
                    >
                        <span
                            className={`page-link pagination-link-active ${
                                i === page ? "bg-main-color text-white" : ""
                            }`}
                        >
                            {i}
                        </span>
                    </li>
                );
            }
        } else {
            pages.push(
                <li
                    key={1}
                    className="page-item mb-0"
                    onClick={() => handlePageChange(1)}
                >
                    <span
                        className={`page-link pagination-link-active ${
                            1 === page ? "bg-main-color text-white" : ""
                        }`}
                    >
                        1
                    </span>
                </li>
            );

            if (page > maxVisiblePages) {
                pages.push(
                    <li key="start-dots" className="page-item mb-0 disabled">
                        <span className="page-link pagination-link-active">
                            ...
                        </span>
                    </li>
                );
            }

            const startPage = Math.max(
                2,
                page - Math.floor(maxVisiblePages / 2)
            );
            const endPage = Math.min(
                totalPages - 1,
                startPage + maxVisiblePages - 1
            );

            for (let i = startPage; i <= endPage; i++) {
                pages.push(
                    <li
                        key={i}
                        className="page-item mb-0"
                        onClick={() => handlePageChange(i)}
                    >
                        <span
                            className={`page-link pagination-link-active ${
                                i === page ? "bg-main-color text-white" : ""
                            }`}
                        >
                            {i}
                        </span>
                    </li>
                );
            }

            if (endPage < totalPages - 1) {
                pages.push(
                    <li key="end-dots" className="page-item mb-0 disabled">
                        <span className="page-link pagination-link-active">
                            ...
                        </span>
                    </li>
                );
            }

            pages.push(
                <li
                    key={totalPages}
                    className="page-item mb-0"
                    onClick={() => handlePageChange(totalPages)}
                >
                    <span
                        className={`page-link pagination-link-active ${
                            totalPages === page
                                ? "bg-main-color text-white"
                                : ""
                        }`}
                    >
                        {totalPages}
                    </span>
                </li>
            );
        }

        return pages;
        // eslint-disable-next-line react-hooks/exhaustive-deps        
    }, [page, totalPages]);
    return (
        <section id="explore_area" className="section_padding">
            {availabilityError && (
                <div className="mx-auto w-full max-w-[1440px] px-3 lg:px-0 h-screen">
                    <div className="flex rounded-md bg-red-100 border text-red-700 border-red-700 p-4 w-full items-center">
                        {t("sorryNoflightsFound")}
                    </div>
                </div>
            )}
            {!availabilityError && (
                <div
                    ref={elementRef}
                    className="mx-auto w-full max-w-[1440px] px-3 lg:px-0"
                >
                    {availabilityLoading ? (
                        <SectionHeading heading={t("searchingForFlights")} />
                    ) : (
                        <SectionHeading
                            heading={
                                t("flightsFound") + " " +filteredFlights?.length
                            }
                        />
                    )}
                    <div className="row   ps-3 pe-0 md:pe-3 my-4 block  w-full">
                        {isMobile && availabilityResults?.allFlightCombinations && (
                            <div
                                onClick={() => setIsOpenFilters(!isOpenFilters)}
                                className="flex gap-1 items-center justify-center px-4 py-1 font-semibold cursor-pointer bg-secondary-color text-white w-full  md:w-fit border border-secondary-color  rounded-md"
                            >
                                <BiFilterAlt />
                                Filters
                            </div>
                        )}
                    </div>
                    <div className="row">
                        <div className="hidden lg:block col-lg-3">
                            {availabilityLoading ? (
                                <div className="h-screen">
                                    <div className="flight-card-skeleton">
                                        <span className="skeletonLoading"></span>
                                    </div>
                                </div>
                            ) : (
                                <SideBar
                                    filters={filters}
                                    setFilters={setFilters}
                                    allFlights={baseFlights}
                                />
                            )}
                        </div>
                        <div className="col-lg-9">
                            <div className="row">
                                <div className="col-lg-12">
                                    <div className="flight_search_result_wrapper">
                                        {!availabilityLoading && baseFlights?.length > 0 && (
                                            <AirlinePriceComparison
                                                flights={baseFlights}
                                                onSelectAirline={handleSelectAirline}
                                                selectedAirlines={filters.selectedAirlines}
                                            />
                                        )}
                                        {availabilityLoading
                                            ? [1, 2, 3, 4, 5].map(
                                                (_, index) => (
                                                  <div
                                                      className="h-[200px] mb-4"
                                                      key={index}
                                                  >
                                                      <div className="flight-card-skeleton ">
                                                          <span className="skeletonLoading"></span>
                                                      </div>
                                                  </div>
                                              )
                                            )
                                            : paginatedFlights?.map(
                                                  (flight, index) => (
                                                      <FlightCard
                                                          flight={flight}
                                                          index={index}
                                                          key={index}
                                                      />
                                                  )
                                              )}
                                    </div>
                                    <nav
                                        className="d-flex mt-16 px-2 justify-content-center overflow-x-auto"
                                        aria-label="navigation"
                                    >
                                        {totalPages > 1 && (
                                            <ul className="flex gap-2 items-center">
                                                <li
                                                    className={`${
                                                        page === 1
                                                            ? "disabled"
                                                            : ""
                                                    }`}
                                                    onClick={() =>
                                                        handlePageChange(
                                                            page - 1
                                                        )
                                                    }
                                                >
                                                    <span className="group page-link px-2 py-2 border-main-color  rounded-md  text-main-color hover:bg-main-color hover:text-white">
                                                        <FaAngleLeft className="text-main-color group-hover:text-white duration-100 cursor-pointer " />
                                                    </span>
                                                </li>
                                                {renderPagination}
                                                <li
                                                    className={`${
                                                        page === totalPages
                                                            ? "disabled"
                                                            : ""
                                                    }`}
                                                    onClick={() =>
                                                        handlePageChange(
                                                            page + 1
                                                        )
                                                    }
                                                >
                                                    <span className="group page-link px-2 py-2 border-main-color  rounded-md  text-main-color hover:bg-main-color hover:text-white">
                                                        <FaAngleRight className="text-main-color group-hover:text-white duration-100 cursor-pointer " />
                                                    </span>
                                                </li>
                                            </ul>
                                        )}
                                    </nav>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {isMobile && availabilityResults?.allFlightCombinations && (
                <MobileFilters
                    isOpen={isOpenFilters}
                    toggle={() => setIsOpenFilters(!isOpenFilters)}
                    filters={filters}
                    setFilters={setFilters}
                    allFlights={baseFlights}
                />
            )}
        </section>
    );
};

export default FlightSearchWrapper;
